import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { endpoints, formatApiError } from "../../lib/api";
import { healthMeta, daysAgo, STAGE_LABELS } from "../../lib/crmHealth";
import { typeMeta } from "../../lib/accountTypes";
import { HealthChip, QuickLogContact } from "../../components/ContactHealth";
import { CardScanButton } from "../../components/CardScanner";
import {
    ChartLineUp, ArrowsClockwise, Gear, PhoneCall, ArrowSquareOut, CheckCircle, CalendarCheck,
    ListChecks, WarningCircle, Users, Handshake, X, CircleNotch, Phone,
} from "@phosphor-icons/react";

const NEW_STAGES = ["lead", "contacted", "proposal", "negotiation"];
const AGE_BUCKETS = [
    { key: "0-14", label: "0–14d", test: (d) => d !== null && d <= 14, color: "#00853F" },
    { key: "15-30", label: "15–30d", test: (d) => d !== null && d > 14 && d <= 30, color: "#65a30d" },
    { key: "31-60", label: "31–60d", test: (d) => d !== null && d > 30 && d <= 60, color: "#d97706" },
    { key: "61-90", label: "61–90d", test: (d) => d !== null && d > 60 && d <= 90, color: "#ea580c" },
    { key: "90+", label: "90d+", test: (d) => d !== null && d > 90, color: "#dc2626" },
    { key: "never", label: "Never", test: (d) => d === null, color: "#7f1d1d" },
];
const STALE_IN_STAGE_DAYS = 30;

export default function CrmDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);
    const [ownerFilter, setOwnerFilter] = useState("all");
    const [listTab, setListTab] = useState("needs");
    const [loggingId, setLoggingId] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);

    async function load() {
        setLoading(true);
        try { const { data } = await endpoints.crmOverview(); setData(data); setErr(""); }
        catch (e) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);
    useEffect(() => {
        // Default to "My accounts" when the user owns any.
        if (data && ownerFilter === "all" && data.accounts.some((a) => a.owner_id === user?.id)) setOwnerFilter("mine");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const owners = useMemo(() => {
        const m = new Map();
        (data?.accounts || []).forEach((a) => { if (a.owner_id) m.set(a.owner_id, a.owner || "Unknown"); });
        return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
    }, [data]);

    const matchOwner = (ownerId) => ownerFilter === "all" ? true
        : ownerFilter === "mine" ? ownerId === user?.id
        : ownerFilter === "none" ? !ownerId : ownerId === ownerFilter;

    const accounts = useMemo(() => (data?.accounts || []).filter((a) => matchOwner(a.owner_id)), [data, ownerFilter]); // eslint-disable-line
    const fups = useMemo(() => (data?.followups || []).filter((f) => matchOwner(f.account_owner_id)), [data, ownerFilter]); // eslint-disable-line
    const tracked = accounts.filter((a) => a.status !== "off");
    const needs = tracked.filter((a) => a.status === "overdue" || a.status === "never").sort((a, b) => b.days_overdue - a.days_overdue);
    const soon = tracked.filter((a) => a.status === "due_soon");
    const newBiz = accounts.filter((a) => a.segment === "new_business");
    const existing = accounts.filter((a) => a.segment === "existing");
    const staleLeads = newBiz.filter((a) => a.days_in_stage > STALE_IN_STAGE_DAYS);
    const existingOk = existing.filter((a) => a.status === "ok" || a.status === "due_soon" || a.status === "off");
    const fOverdue = fups.filter((f) => f.bucket === "overdue");
    const fToday = fups.filter((f) => f.bucket === "today");
    const fWeek = fups.filter((f) => f.bucket === "week");

    const listRows = listTab === "needs" ? needs : listTab === "soon" ? soon
        : [...tracked].sort((a, b) => (b.days_since_contact ?? 9999) - (a.days_since_contact ?? 9999));

    async function completeFollowup(f) {
        try {
            if (f.kind === "task") await endpoints.updateClientTask(f.account_id, f.id, { status: "done" });
            else await endpoints.updateClientAppointment(f.account_id, f.id, { status: "done" });
            load();
        } catch (e) { alert(formatApiError(e)); }
    }

    if (err) return <div className="max-w-3xl mx-auto px-6 py-16 text-center text-[var(--rom-danger)]">{err}</div>;

    return (
        <div className="px-6 py-8 space-y-5 max-w-[1500px]" data-testid="crm-dashboard-page">
            <header className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">CRM</div>
                    <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-3">
                        <ChartLineUp size={28} weight="bold" className="text-[var(--rom-orange)]" /> Relationship dashboard
                    </h1>
                    <p className="text-sm text-[var(--rom-text-2)] mt-1">Who needs a call, what's overdue, and how fresh every relationship is.</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} data-testid="crm-owner-filter"
                        className="h-9 px-3 rounded-[4px] border border-[var(--rom-border-strong)] text-sm font-semibold bg-white">
                        <option value="all">All accounts</option>
                        <option value="mine">My accounts</option>
                        {owners.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                        <option value="none">Unassigned</option>
                    </select>
                    <CardScanButton onDone={load} />
                    <button className="btn btn-ghost" onClick={() => setSettingsOpen(true)} data-testid="crm-cadence-settings-btn"><Gear size={14} weight="bold" /> Cadence</button>
                    <button className="btn btn-ghost" onClick={load} data-testid="crm-dashboard-refresh"><ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh</button>
                </div>
            </header>

            {!data ? <div className="text-center text-[var(--rom-text-3)] py-16">Loading…</div> : (
                <>
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="crm-tiles">
                        <Tile label="Need contact" value={needs.length} sub={`of ${tracked.length} tracked accounts`} accent={needs.length ? "#dc2626" : undefined} icon={PhoneCall} onClick={() => setListTab("needs")} testId="tile-needs-contact" />
                        <Tile label="Overdue follow-ups" value={fOverdue.length} sub={`${fToday.length} due today`} accent={fOverdue.length ? "#dc2626" : undefined} icon={ListChecks} testId="tile-overdue-followups" />
                        <Tile label="Due this week" value={fWeek.length + soon.length} sub={`${fWeek.length} follow-ups · ${soon.length} contacts`} accent={fWeek.length + soon.length ? "#d97706" : undefined} icon={CalendarCheck} testId="tile-due-week" />
                        <Tile label="New-business leads" value={newBiz.length} sub={staleLeads.length ? `${staleLeads.length} stuck >${STALE_IN_STAGE_DAYS}d in stage` : "none stuck"} icon={Handshake} testId="tile-new-business" />
                        <Tile label="Existing accounts on track" value={existing.length ? `${Math.round((existingOk.length / existing.length) * 100)}%` : "—"} sub={`${existingOk.length} of ${existing.length} on supplier list / active`} accent={existing.length && existingOk.length / existing.length < 0.7 ? "#d97706" : "#00853F"} icon={Users} testId="tile-existing-health" />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                        <div className="lg:col-span-2 rom-card" data-testid="crm-contact-list">
                            <div className="rom-card-head">
                                <h3 className="flex items-center gap-1.5"><PhoneCall size={14} weight="bold" /> Contact list</h3>
                                <div className="inline-flex rounded-[6px] border border-[var(--rom-border-strong)] overflow-hidden text-xs font-semibold">
                                    {[["needs", `Needs contact (${needs.length})`], ["soon", `Due soon (${soon.length})`], ["all", "All tracked"]].map(([k, l]) => (
                                        <button key={k} onClick={() => setListTab(k)} data-testid={`crm-list-tab-${k}`}
                                            className={`px-3 py-1.5 ${listTab === k ? "bg-[var(--rom-navy)] text-white" : "bg-white text-[var(--rom-text-2)] hover:bg-slate-50"}`}>{l}</button>
                                    ))}
                                </div>
                            </div>
                            <div className="rom-card-body p-0">
                                {listRows.length === 0 ? (
                                    <div className="text-sm text-[var(--rom-text-3)] text-center py-10">
                                        {listTab === "needs" ? "Nobody is overdue for contact." : "Nothing here."}
                                    </div>
                                ) : (
                                    <div className="divide-y divide-[var(--rom-border)] max-h-[560px] overflow-y-auto">
                                        {listRows.map((a) => {
                                            const T = typeMeta(a.type).icon;
                                            return (
                                                <div key={a.id} className="px-4 py-2.5" data-testid={`crm-row-${a.id}`}>
                                                    <div className="flex items-center gap-3">
                                                        <div className="min-w-0 flex-1">
                                                            <div className="font-semibold text-sm flex items-center gap-1.5 truncate">
                                                                <T size={12} weight="bold" className="text-[var(--rom-text-3)] shrink-0" />
                                                                <button className="hover:underline truncate text-left" onClick={() => navigate(`/crm/accounts/${a.id}`)}>{a.name}</button>
                                                                {a.parent_name && <span className="font-normal text-[var(--rom-text-3)] text-xs truncate">· {a.parent_name}</span>}
                                                            </div>
                                                            <div className="text-[11px] text-[var(--rom-text-2)] flex items-center gap-2 flex-wrap mt-0.5">
                                                                <span>{STAGE_LABELS[a.stage] || a.stage}</span>
                                                                {a.primary_contact && <span>· {a.primary_contact}</span>}
                                                                {a.phone && <a href={`tel:${a.phone}`} className="flex items-center gap-0.5 hover:underline"><Phone size={10} /> {a.phone}</a>}
                                                                <span className="text-[var(--rom-text-3)]">· {a.owner || "Unassigned"}</span>
                                                            </div>
                                                        </div>
                                                        <div className="text-right shrink-0 hidden sm:block">
                                                            <div className="text-xs font-semibold">{a.last_contact_at ? daysAgo(a.days_since_contact) : "Never"}</div>
                                                            <div className="text-[10px] text-[var(--rom-text-3)]">every {a.cadence_days}d</div>
                                                        </div>
                                                        <div className="w-[118px] shrink-0 text-right"><HealthChip health={a} compact /></div>
                                                        <div className="flex items-center gap-1 shrink-0">
                                                            <button className="btn btn-ghost btn-sm" title="Log a contact" onClick={() => setLoggingId(loggingId === a.id ? null : a.id)} data-testid={`crm-log-${a.id}`}><PhoneCall size={12} weight="bold" /></button>
                                                            <button className="btn btn-ghost btn-sm" title="Open account" onClick={() => navigate(`/crm/accounts/${a.id}`)}><ArrowSquareOut size={12} weight="bold" /></button>
                                                        </div>
                                                    </div>
                                                    {loggingId === a.id && (
                                                        <div className="mt-2"><QuickLogContact clientId={a.id} onLogged={() => { setLoggingId(null); load(); }} onCancel={() => setLoggingId(null)} /></div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="rom-card" data-testid="crm-followups">
                            <div className="rom-card-head"><h3 className="flex items-center gap-1.5"><ListChecks size={14} weight="bold" /> Follow-ups</h3></div>
                            <div className="rom-card-body space-y-4 max-h-[600px] overflow-y-auto">
                                {fups.length === 0 && <div className="text-sm text-[var(--rom-text-3)] text-center py-8">No follow-ups due in the next 7 days.</div>}
                                <FollowupGroup title="Overdue" items={fOverdue} color="#dc2626" today={data.today} onDone={completeFollowup} navigate={navigate} />
                                <FollowupGroup title="Today" items={fToday} color="#d97706" today={data.today} onDone={completeFollowup} navigate={navigate} />
                                <FollowupGroup title="Next 7 days" items={fWeek} color="#00853F" today={data.today} onDone={completeFollowup} navigate={navigate} />
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        <AgeingCard newBiz={newBiz} existing={existing} />
                        <PipelineCard newBiz={newBiz} existing={existing} navigate={navigate} />
                    </div>
                </>
            )}

            {settingsOpen && <CadenceSettings canEdit={["admin", "manager"].includes(user?.role)} onClose={(changed) => { setSettingsOpen(false); if (changed) load(); }} />}
        </div>
    );
}

function Tile({ label, value, sub, accent, icon: Icon, onClick, testId }) {
    return (
        <button onClick={onClick} disabled={!onClick} className="rom-card text-left disabled:cursor-default" data-testid={testId}>
            <div className="rom-card-body">
                <div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold flex items-center gap-1.5"><Icon size={12} weight="bold" /> {label}</div>
                <div className="font-display font-black text-3xl mt-1" style={{ color: accent || "var(--rom-text)" }}>{value}</div>
                <div className="text-[11px] text-[var(--rom-text-2)] mt-0.5">{sub}</div>
            </div>
        </button>
    );
}

function FollowupGroup({ title, items, color, today, onDone, navigate }) {
    if (!items.length) return null;
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider font-bold mb-1.5" style={{ color }}>{title} · {items.length}</div>
            <div className="space-y-1.5">
                {items.map((f) => {
                    const late = f.due < today ? Math.round((new Date(today) - new Date(f.due)) / 86400000) : 0;
                    const Icon = f.kind === "task" ? ListChecks : CalendarCheck;
                    return (
                        <div key={`${f.kind}-${f.id}`} className="flex items-start gap-2 p-2 border border-[var(--rom-border)] rounded-[4px]" data-testid={`crm-followup-${f.id}`}>
                            <Icon size={13} weight="bold" className="mt-0.5 shrink-0 text-[var(--rom-navy)]" />
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium leading-snug">{f.title}</div>
                                <button className="text-[11px] text-[var(--rom-navy)] hover:underline truncate block max-w-full text-left" onClick={() => navigate(`/crm/accounts/${f.account_id}`)}>{f.account_name}</button>
                                <div className="text-[10px] text-[var(--rom-text-3)]">
                                    {f.due}{late ? ` · ${late}d late` : ""}{f.assignee ? ` · ${f.assignee}` : ""}{f.detail ? ` · ${f.detail}` : ""}
                                </div>
                            </div>
                            <button onClick={() => onDone(f)} title={f.kind === "task" ? "Mark complete" : "Mark visit done (counts as contact)"} className="text-[var(--rom-text-3)] hover:text-[var(--rom-green)] shrink-0" data-testid={`crm-followup-done-${f.id}`}>
                                <CheckCircle size={18} weight="bold" />
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function AgeingCard({ newBiz, existing }) {
    const rows = [["New business", newBiz], ["Existing accounts", existing]];
    return (
        <div className="rom-card" data-testid="crm-ageing">
            <div className="rom-card-head"><h3 className="flex items-center gap-1.5"><WarningCircle size={14} weight="bold" /> Contact ageing — days since last contact</h3></div>
            <div className="rom-card-body space-y-4">
                {rows.map(([label, list]) => {
                    const counts = AGE_BUCKETS.map((b) => ({ ...b, n: list.filter((a) => b.test(a.days_since_contact ?? null)).length }));
                    const total = list.length || 1;
                    return (
                        <div key={label}>
                            <div className="flex items-center justify-between text-xs font-semibold mb-1"><span>{label}</span><span className="text-[var(--rom-text-3)]">{list.length} accounts</span></div>
                            <div className="flex h-6 rounded-[4px] overflow-hidden bg-slate-100">
                                {counts.filter((b) => b.n).map((b) => (
                                    <div key={b.key} title={`${b.label}: ${b.n}`} style={{ width: `${(b.n / total) * 100}%`, background: b.color }}
                                        className="text-[10px] text-white font-bold flex items-center justify-center">{b.n}</div>
                                ))}
                            </div>
                        </div>
                    );
                })}
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px] text-[var(--rom-text-2)]">
                    {AGE_BUCKETS.map((b) => <span key={b.key} className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: b.color }} /> {b.label}</span>)}
                </div>
            </div>
        </div>
    );
}

function PipelineCard({ newBiz, existing, navigate }) {
    const health = ["ok", "due_soon", "overdue", "never", "off"].map((s) => ({ s, n: existing.filter((a) => a.status === s).length })).filter((x) => x.n);
    return (
        <div className="rom-card" data-testid="crm-pipeline-split">
            <div className="rom-card-head">
                <h3 className="flex items-center gap-1.5"><Handshake size={14} weight="bold" /> New business vs existing accounts</h3>
                <button className="btn btn-ghost btn-sm" onClick={() => navigate("/crm")}>Open pipeline <ArrowSquareOut size={11} /></button>
            </div>
            <div className="rom-card-body space-y-4">
                <div>
                    <div className="text-xs font-semibold mb-1.5">New-business pipeline</div>
                    <div className="grid grid-cols-4 gap-2">
                        {NEW_STAGES.map((s) => {
                            const list = newBiz.filter((a) => a.stage === s);
                            const avg = list.length ? Math.round(list.reduce((n, a) => n + a.days_in_stage, 0) / list.length) : 0;
                            const stuck = list.filter((a) => a.days_in_stage > STALE_IN_STAGE_DAYS).length;
                            return (
                                <div key={s} className="border border-[var(--rom-border)] rounded-[6px] p-2.5" data-testid={`crm-stage-${s}`}>
                                    <div className="text-[10px] uppercase tracking-wider text-[var(--rom-text-3)] font-semibold">{STAGE_LABELS[s]}</div>
                                    <div className="font-display font-black text-2xl">{list.length}</div>
                                    <div className="text-[10px] text-[var(--rom-text-2)]">{list.length ? `avg ${avg}d in stage` : "—"}</div>
                                    {stuck > 0 && <div className="text-[10px] font-bold text-[#d97706]">{stuck} stuck</div>}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div>
                    <div className="text-xs font-semibold mb-1.5">Existing accounts (supplier list & active) — relationship health</div>
                    {existing.length === 0 ? <div className="text-sm text-[var(--rom-text-3)]">No existing accounts yet.</div> : (
                        <div className="flex flex-wrap gap-2">
                            {health.map(({ s, n }) => {
                                const m = healthMeta(s);
                                return <span key={s} className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ color: m.color, background: m.bg }}>{m.label}: {n}</span>;
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function CadenceSettings({ canEdit, onClose }) {
    const [vals, setVals] = useState(null);
    const [defaults, setDefaults] = useState({});
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    useEffect(() => {
        endpoints.crmSettings().then(({ data }) => { setVals(data.cadence_days); setDefaults(data.defaults); }).catch((e) => setErr(formatApiError(e)));
    }, []);
    async function save() {
        setBusy(true); setErr("");
        try { await endpoints.saveCrmSettings({ cadence_days: vals }); onClose(true); }
        catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
    }
    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="cadence-modal">
            <div className="rom-card w-full max-w-md">
                <div className="rom-card-head"><h3 className="flex items-center gap-1.5"><Gear size={14} weight="bold" /> Contact cadence by stage</h3><button onClick={() => onClose(false)}><X size={18} /></button></div>
                <div className="rom-card-body space-y-3">
                    <p className="text-xs text-[var(--rom-text-2)]">Flag an account when nobody has called, emailed or visited within this many days. 0 = don't flag. Any account can override this on its own page.</p>
                    {!vals ? <div className="text-sm text-[var(--rom-text-3)]">Loading…</div> : (
                        <div className="grid grid-cols-2 gap-2">
                            {Object.keys(defaults).map((s) => (
                                <label key={s} className="flex items-center justify-between gap-2 text-sm border border-[var(--rom-border)] rounded-[4px] px-2.5 py-1.5">
                                    <span>{STAGE_LABELS[s] || s}</span>
                                    <span className="flex items-center gap-1">
                                        <input type="number" min={0} max={365} disabled={!canEdit} value={vals[s] ?? ""} data-testid={`cadence-input-${s}`}
                                            onChange={(e) => setVals((v) => ({ ...v, [s]: e.target.value }))}
                                            className="w-16 h-7 px-1.5 rounded-[4px] border border-[var(--rom-border-strong)] text-right text-sm" />
                                        <span className="text-[11px] text-[var(--rom-text-3)]">days</span>
                                    </span>
                                </label>
                            ))}
                        </div>
                    )}
                    {!canEdit && <p className="text-[11px] text-[var(--rom-text-3)]">Only managers and admins can change these.</p>}
                    {err && <div className="text-xs text-red-600">{err}</div>}
                    <div className="flex justify-end gap-2">
                        <button className="btn btn-ghost" onClick={() => onClose(false)}>Close</button>
                        {canEdit && <button className="btn btn-primary" onClick={save} disabled={busy || !vals} data-testid="cadence-save-btn">{busy ? <CircleNotch size={14} className="animate-spin" /> : "Save"}</button>}
                    </div>
                </div>
            </div>
        </div>
    );
}
